// :style="`background-color: ${merchant?.profile?.theme?.primaryColor}30`"
// Utilities
import { Merchant, MerchantTheme } from '@authvia-v3/pinia-stores-user';
import { defineStore } from 'pinia';

import { syncAllCreditCardVuetifyOverrides } from '@/utils/creditCardVuetifyOverridesSync';
import {
  applyContrastButtonTokens,
  applyMerchantTextOnLightToken,
  buildCreditCardVuetifyOverrides,
  clearContrastButtonTokens,
  clearMerchantTextOnLightToken,
  syncAllActionContrastRoots,
  syncAllAvwcContrastTokens,
  syncPaymentMethodDialogPortalTokens,
} from '@/utils/colorContrast';

function applyMerchantButtonTokens(primaryColor: string): void {
  const root = document.documentElement.style;

  applyContrastButtonTokens(root, primaryColor);
  applyMerchantTextOnLightToken(root, primaryColor);
  root.setProperty('--item-theme-color', primaryColor, 'important');

  syncAllAvwcContrastTokens();
  syncAllActionContrastRoots();

  const pmDialogPortal = document.getElementById('authvia-pm-dialog-portal');
  if (pmDialogPortal) {
    syncPaymentMethodDialogPortalTokens(pmDialogPortal);
  }
}

function clearMerchantButtonTokens(): void {
  const root = document.documentElement.style;

  clearContrastButtonTokens(root);
  clearMerchantTextOnLightToken(root);
  root.removeProperty('--item-theme-color');
}

export const useMerchantBrandingStore = defineStore('merchantBranding', {
  state: () => ({
    /** Plain CSS for Authvia `data-vuetify-overrides` / `vuetify-overrides` props. */
    creditCardVuetifyOverrides: '',
  }),
  actions: {
    change(merchant: Merchant | undefined) {
      const theme: MerchantTheme | undefined = merchant?.profile?.theme;

      if (theme?.primaryColor) {
        this.creditCardVuetifyOverrides = buildCreditCardVuetifyOverrides(
          theme.primaryColor,
        );
        /**
         * Give variables for all children to adopt and use for merchant branding.
         */
        document.documentElement.style.setProperty(
          '--merchant-background-color',
          `${theme.primaryColor}20`,
          'important',
        );
        document.documentElement.style.setProperty(
          '--merchant-border-rounding',
          `${theme.borderRadius}`,
          'important',
        );
        document.documentElement.style.setProperty(
          '--merchant-border-color',
          `${theme.primaryColor}`,
          'important',
        );
        document.documentElement.style.setProperty(
          '--merchant-color-lighter-4',
          `${theme.primaryColor}20`,
          'important',
        );
        document.documentElement.style.setProperty(
          '--merchant-color-lighter-3',
          `${theme.primaryColor}40`,
          'important',
        );
        document.documentElement.style.setProperty(
          '--merchant-color-lighter-2',
          `${theme.primaryColor}60`,
          'important',
        );
        document.documentElement.style.setProperty(
          '--merchant-color-lighter-1',
          `${theme.primaryColor}80`,
          'important',
        );
        document.documentElement.style.setProperty(
          '--merchant-color',
          `${theme.primaryColor}`,
          'important',
        );
        applyMerchantButtonTokens(theme.primaryColor);
        syncAllCreditCardVuetifyOverrides();
      } else {
        this.creditCardVuetifyOverrides = '';

        // Clear inline branding before sync so readers treat brand as inactive.
        document.documentElement.style.setProperty(
          '--merchant-background-color',
          '',
        );
        document.documentElement.style.setProperty(
          '--merchant-border-rounding',
          '',
        );
        document.documentElement.style.setProperty(
          '--merchant-border-color',
          '',
        );
        document.documentElement.style.setProperty(
          '--merchant-color-lighter-4',
          '',
        );
        document.documentElement.style.setProperty(
          '--merchant-color-lighter-3',
          '',
        );
        document.documentElement.style.setProperty(
          '--merchant-color-lighter-2',
          '',
        );
        document.documentElement.style.setProperty(
          '--merchant-color-lighter-1',
          '',
        );
        document.documentElement.style.setProperty('--merchant-color', '');

        clearMerchantButtonTokens();
        syncAllAvwcContrastTokens();
        syncAllActionContrastRoots();

        const pmDialogPortal = document.getElementById('authvia-pm-dialog-portal');
        if (pmDialogPortal) {
          syncPaymentMethodDialogPortalTokens(pmDialogPortal);
        }

        syncAllCreditCardVuetifyOverrides();
      }
    },
  },
});
